/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Button, Col, Divider, Input, message, Popconfirm, Row, Upload } from 'antd';

import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';

import { filterOsDealFields, OS_DEAL_FIELDS } from '../../ScheduleConecta/Components/osDealFields';
import {
  isAcceptedOsCatalogFile,
  isPdfOsFile,
  MAX_OS_UPLOAD_BYTES,
  WORD_LIKE_EXTENSIONS,
} from '../../ScheduleConecta/Components/OsUploadDownloadSection';

const OS_CATALOG_ACCEPT = [...WORD_LIKE_EXTENSIONS, '.pdf'];
const OS_CATALOG_KIND_PDF = 2;

function isOsPdfCatalogItem(item) {
  return item?.kind === OS_CATALOG_KIND_PDF || isPdfOsFile(item?.label);
}

function isValidOsCatalogUpload(file) {
  return isAcceptedOsCatalogFile(file?.name) && file?.size > 0 && file.size <= MAX_OS_UPLOAD_BYTES;
}

function notifyRejectedOsCatalogFile(file) {
  if (!isAcceptedOsCatalogFile(file?.name)) {
    message.error('Envie apenas arquivos Word e similares ou PDF.');
    return;
  }
  message.error('O arquivo deve ter até 20 MB.');
}

function sanitizeOsDownloadName(fileName) {
  const baseName = String(fileName || 'OS')
    .split(/[/\\]/)
    .pop()
    .replace(/[?#"']/g, '')
    .trim();
  return baseName || 'OS.docx';
}

function startLocalFileDownload(blob, fileName) {
  const safeName = sanitizeOsDownloadName(fileName);
  const fileBlob = new Blob([blob], { type: 'application/octet-stream' });
  const objectUrl = window.URL.createObjectURL(fileBlob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = safeName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  }, 1500);
}

export async function downloadOsCatalogOriginal(item) {
  const response = await fetch(item.url);
  if (!response.ok) {
    throw new Error('ERR_OS_GERACAO_OU_DOWNLOAD_FALHOU');
  }

  const blob = await response.blob();
  if (!blob.size) {
    throw new Error('ERR_OS_GERACAO_OU_DOWNLOAD_FALHOU');
  }

  startLocalFileDownload(blob, item.label || item.name);
}

function OsSettings({ osTemplates = [], isSaving = false, onFilesSelected, onRemoveItem }) {
  const [searchText, setSearchText] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const visibleFields = filterOsDealFields(OS_DEAL_FIELDS, searchText)
    .slice()
    .sort((left, right) => left.label.localeCompare(right.label, 'pt-BR', { sensitivity: 'base' }));

  const copyVariable = (variable) => {
    navigator.clipboard
      .writeText(variable)
      .then(() => {
        message.info('Copiado para área de transferência!');
      })
      .catch(() => {
        message.error('Oops. Algo deu errado ao copiar o campo!');
      });
  };

  const beforeUpload = (file, fileList) => {
    if (isSaving) return Upload.LIST_IGNORE;

    const isAccepted = isValidOsCatalogUpload(file);
    if (!isAccepted) notifyRejectedOsCatalogFile(file);

    const isLastFile = file === fileList[fileList.length - 1];
    if (isLastFile) {
      const acceptedFiles = fileList.filter(isValidOsCatalogUpload);
      if (acceptedFiles.length > 0) onFilesSelected(acceptedFiles);
    }

    return isAccepted ? false : Upload.LIST_IGNORE;
  };

  const downloadCatalogItem = async (item) => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      await downloadOsCatalogOriginal(item);
    } catch (error) {
      message.error('Não foi possível baixar o arquivo.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Col span={24}>
        <Divider className="configuration-divider" orientation="left">
          Campos
        </Divider>
      </Col>

      <Col span={8}>
        <Input
          placeholder="Buscar campo"
          allowClear
          value={searchText}
          prefix={<SearchOutlined />}
          onChange={(event) => setSearchText(event.target.value)}
        />
      </Col>

      <Col span={24}>
        <Row gutter={[24, 12]} style={{ marginTop: 16 }}>
          {visibleFields.map((field) => (
            <Col span={12} key={field.name}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <CopyOutlined
                  role="button"
                  aria-label={`Copiar ${field.label}`}
                  onClick={() => copyVariable(field.variable)}
                />
                <strong>{field.label}</strong>
              </div>
            </Col>
          ))}
        </Row>
      </Col>

      <Col span={24}>
        <Divider className="configuration-divider" orientation="left">
          Upload de OS
        </Divider>
      </Col>

      <Col span={24}>
        <Upload
          accept={OS_CATALOG_ACCEPT.join(',')}
          multiple
          showUploadList={false}
          beforeUpload={beforeUpload}
          disabled={isSaving}
        >
          <Button icon={<UploadOutlined />} loading={isSaving} disabled={isSaving}>
            Selecionar arquivo
          </Button>
        </Upload>
        <div role="list" aria-label="Anexos de ordem de serviço" style={{ marginTop: 8 }}>
          {osTemplates.map((item) => (
            <div
              role="listitem"
              key={item.id}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginTop: 8,
                wordBreak: 'break-word',
              }}
            >
              <span>{item.label}</span>
              {isOsPdfCatalogItem(item) && (
                <Button
                  htmlType="button"
                  aria-label={`Download de ${item.label}`}
                  icon={<DownloadOutlined />}
                  loading={isDownloading}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    downloadCatalogItem(item);
                  }}
                />
              )}
              <Popconfirm
                title="Excluir este anexo?"
                okText="Excluir"
                cancelText="Cancelar"
                onConfirm={() => {
                  const currentItem = item;
                  window.setTimeout(() => onRemoveItem?.(currentItem), 0);
                }}
              >
                <Button
                  htmlType="button"
                  aria-label={`Excluir ${item.label}`}
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </div>
          ))}
        </div>
      </Col>
    </>
  );
}

export default OsSettings;
